import React, { createContext, useContext, useMemo, useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

export type GameVersion = '6' | '7';

interface GameVersionContextType {
  gameVersion: GameVersion;
  setGameVersion: (version: GameVersion) => void;
  switchVersion: (version: GameVersion) => void;
}

const GameVersionContext = createContext<GameVersionContextType | undefined>(undefined);

export const useGameVersion = () => {
  const context = useContext(GameVersionContext);
  if (!context) {
    throw new Error('useGameVersion must be used within a GameVersionProvider');
  }
  return context;
};

interface GameVersionProviderProps {
  children: ReactNode;
}

export const GameVersionProvider: React.FC<GameVersionProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUserProfile } = useAuth();

  // Derive version directly from URL path - no state, no flash
  const gameVersion = useMemo<GameVersion>(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts[0] === '7') return '7';
    if (pathParts[0] === '6') return '6';
    // For root or non-versioned paths, check localStorage
    const saved = localStorage.getItem('gameVersion');
    if (saved === '6' || saved === '7') return saved;
    return '7';
  }, [location.pathname]);

  // Save to localStorage and update CSS variable when version changes
  useEffect(() => {
    localStorage.setItem('gameVersion', gameVersion);
    document.documentElement.setAttribute('data-version', gameVersion);
  }, [gameVersion]);

  const setGameVersion = (version: GameVersion) => {
    localStorage.setItem('gameVersion', version);
  };

  // Switch version and navigate to the same page in new version
  const switchVersion = (version: GameVersion) => {
    const currentPath = location.pathname;
    const pathParts = currentPath.split('/').filter(Boolean);

    // If current path starts with a version, replace it
    if (pathParts[0] === '6' || pathParts[0] === '7') {
      pathParts[0] = version;
    } else {
      // If no version prefix, add it
      pathParts.unshift(version);
    }

    const newPath = '/' + pathParts.join('/');
    setGameVersion(version);
    navigate(newPath);

    // Refresh user profile for the new version
    refreshUserProfile(version);
  };

  return (
    <GameVersionContext.Provider value={{
      gameVersion,
      setGameVersion,
      switchVersion
    }}>
      {children}
    </GameVersionContext.Provider>
  );
};
